import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLenders,
  getLender,
  createLender,
  updateLender,
  getLenderProducts,
  getLenderUsers,
  blockLender,
  unblockLender,
  deleteLender,
  type CreateLenderRequest,
  type UpdateLenderRequest,
} from '../api/lenders';

export function useLenders() {
  return useQuery({
    queryKey: ['lenders'],
    queryFn: getLenders,
  });
}

export function useLender(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn],
    queryFn: () => getLender(urn),
    enabled: !!urn,
  });
}

export function useLenderProducts(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn, 'products'],
    queryFn: () => getLenderProducts(urn),
    enabled: !!urn,
  });
}

export function useLenderUsers(urn: string) {
  return useQuery({
    queryKey: ['lenders', urn, 'users'],
    queryFn: async () => {
      try {
        return await getLenderUsers(urn);
      } catch {
        // Gracefully return empty array if endpoint is not available yet
        return [];
      }
    },
    enabled: !!urn,
    retry: false,
  });
}

export function useCreateLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLenderRequest) => createLender(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useUpdateLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, data }: { urn: string; data: UpdateLenderRequest }) =>
      updateLender(urn, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useBlockLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urn: string) => blockLender(urn),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useUnblockLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (urn: string) => unblockLender(urn),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}

export function useDeleteLender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ urn, confirmUrn }: { urn: string; confirmUrn: string }) =>
      deleteLender(urn, confirmUrn),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lenders'] });
    },
  });
}
